import { FunctionComponent } from 'react';

import {
    Box,
    Button,
    Divider,
    IconButton,
    Popover,
    PopoverProps,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useGetLiftOperatorQuery } from '@/api/v2/liftOperator';
import { useGetB2BUserByIdQuery } from '@/api/v2/b2b';
import { isB2B } from '@/constants';
import { useSlice } from '@/hooks/useSlice';
import { authSlice } from '@starjack/features/authentication';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@starjack/features/notifications';

type AccountPopover = {
    open: boolean;
    anchorEl: PopoverProps['anchorEl'];
    onClose?: () => void;
};

export const AccountPopover: FunctionComponent<AccountPopover> = ({
    anchorEl,
    onClose,
    open,
}) => {
    const {
        state: { userId },
    } = useSlice(authSlice, 'authSlice');
    const { liftOperatorData, logout } = useAuthentication();
    const { t } = useTranslation();
    const { addNotification } = useNotification();

    const { data: liftOperator } = useGetLiftOperatorQuery({
        liftOperatorId: liftOperatorData.liftOperatorId,
    });
    const { data: b2bUserData } = useGetB2BUserByIdQuery(
        {
            b2bUserId: userId,
        },
        {
            skip: !userId && !isB2B,
        }
    );

    const handleSignOut = () => {
        onClose?.();
        logout();
    };

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(
                liftOperatorData.liftOperatorId
            );
            addNotification({
                type: 'success',
                message: t('copiedToClipboard'),
            });
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            addNotification({
                type: 'error',
                message: 'Failed to copy to clipboard',
            });
        }
    };

    return (
        <Popover
            anchorEl={anchorEl}
            onClose={onClose}
            open={open}
            slotProps={{
                paper: {
                    sx: {
                        p: 2,
                        minWidth: 320,
                    },
                },
            }}
        >
            <Box
                sx={{
                    py: 1.5,
                    px: 2,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                    }}
                    variant='subtitle1'
                >
                    {t('account')}
                </Typography>

                <Box>
                    <Typography
                        variant='subtitle2'
                        sx={{
                            fontWeight: 500,
                        }}
                    >
                        {t(isB2B ? 'b2bPartnerCompany' : 'username')}:
                    </Typography>
                    <Typography color='text.secondary' variant='body2'>
                        {isB2B
                            ? b2bUserData?.name
                            : liftOperatorData.skiOperator}
                    </Typography>
                </Box>

                {liftOperator && (
                    <>
                        <Box
                            sx={{
                                mt: 1,
                            }}
                        >
                            <Typography
                                variant='subtitle2'
                                sx={{
                                    fontWeight: 500,
                                    textTransform: 'capitalize',
                                }}
                            >
                                {t('liftoperator')}:
                            </Typography>
                            <Typography color='text.secondary' variant='body2'>
                                {liftOperator.name.de}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                mt: 1,
                            }}
                        >
                            <Typography
                                variant='subtitle2'
                                sx={{
                                    fontWeight: 500,
                                    textTransform: 'capitalize',
                                }}
                            >
                                {t('liftoperatorId')}:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    color='text.secondary'
                                    variant='body2'
                                >
                                    {liftOperatorData.liftOperatorId}
                                </Typography>
                                <IconButton
                                    aria-label='Bergbahn-ID kopieren'
                                    onClick={handleCopyToClipboard}
                                    sx={{
                                        padding: 0.5,
                                    }}
                                >
                                    <ContentCopyIcon
                                        sx={{
                                            fontSize: '1rem',
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
            <Divider
                sx={{
                    my: 1,
                }}
            />

            <Button onClick={handleSignOut} color='inherit'>
                {t('signOut')}
            </Button>
        </Popover>
    );
};
